import React, { useId, useState } from 'react';

/**
 * SmartImage — responsive <picture> with AVIF/WebP/JPEG srcset for
 * object-storage URLs, or Unsplash CDN URLs.
 *
 * Renders:
 *   <picture>
 *     <source type="image/avif" srcset="…w=400&fmt=avif 400w, …w=800&fmt=avif 800w, …">
 *     <source type="image/webp" srcset="…w=400&fmt=webp 400w, …">
 *     <img src="…w=800&fmt=jpeg" srcset="… JPEG variants …" loading="lazy">
 *   </picture>
 *
 * The browser downloads only the SMALLEST variant that fits its screen and
 * decoder support (AVIF-capable browsers get AVIF, older ones fall back to
 * WebP, ancient browsers get JPEG).
 *
 * Props:
 *   - src: relative or absolute URL
 *   - alt, className, sizes: as native
 *   - priority: true → LCP (loading=eager, fetchpriority=high, no lazy)
 *   - aspectRatio: e.g. "3/4" — reserves layout to prevent CLS
 */
const PRODUCT_WIDTHS = [400, 800, 1200, 1600];
const UNSPLASH_WIDTHS = [400, 800, 1200, 1600];

function isObjectStorage(src) {
  return typeof src === 'string' && src.includes('/api/files/');
}
function isUnsplash(src) {
  return typeof src === 'string' && /images\.unsplash\.com/.test(src);
}

function buildObjectStorageSet(src, format) {
  const sep = src.includes('?') ? '&' : '?';
  return PRODUCT_WIDTHS
    .map((w) => `${src}${sep}w=${w}&fmt=${format} ${w}w`)
    .join(', ');
}

function buildUnsplashSet(src) {
  // Unsplash serves WebP/AVIF via ?fm= when supported; safer to use ?auto=format
  return UNSPLASH_WIDTHS
    .map((w) => {
      let u = src.replace(/([?&])w=\d+/g, `$1w=${w}`);
      if (!/([?&])w=\d+/.test(u)) {
        u += (u.includes('?') ? '&' : '?') + `w=${w}`;
      }
      // ensure auto=format is present for CDN-side webp/avif negotiation
      if (!/[?&]auto=/.test(u)) u += '&auto=format';
      return `${u} ${w}w`;
    })
    .join(', ');
}

function getDefaultSrc(src) {
  // Fallback src for the <img> element (mid-size JPEG for compatibility)
  if (isObjectStorage(src)) {
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}w=800&fmt=jpeg`;
  }
  return src;
}

export default function SmartImage({
  src,
  mobileSrc,
  alt = '',
  className = '',
  pictureClassName = '',
  sizes = '100vw',
  priority = false,
  aspectRatio,
  style,
  onClick,
  onError,
  ...rest
}) {
  const [failed, setFailed] = useState(false);
  const fallbackId = useId().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  if (!src) return null;

  const finalStyle = aspectRatio ? { aspectRatio, ...style } : style;
  if (failed) {
    return (
      <div
        role="img"
        aria-label={alt ? `${alt}: immagine non disponibile` : 'Immagine non disponibile'}
        data-testid={`smart-image-fallback-${fallbackId}`}
        className={`${className} flex items-center justify-center bg-[#F4F0E8] text-center text-[#0F3530]`}
        style={finalStyle}
      >
        <div className="px-5">
          <div className="font-serif text-xl tracking-[0.18em] text-[#C9A961]">HARIZI</div>
          <div className="mt-2 text-[9px] font-medium uppercase tracking-[0.22em] text-[#0F3530]/60">
            Immagine non disponibile
          </div>
        </div>
      </div>
    );
  }

  const commonImgProps = {
    alt,
    className,
    loading: priority ? 'eager' : 'lazy',
    decoding: 'async',
    onClick,
    onError: (event) => {
      setFailed(true);
      onError?.(event);
    },
    style: finalStyle,
    ...(priority ? { fetchPriority: 'high' } : {}),
    ...rest,
  };

  // Object storage — <picture> with AVIF + WebP + JPEG, optionally using a
  // distinct art-directed source on mobile.
  if (isObjectStorage(src)) {
    return (
      <picture className={pictureClassName}>
        {mobileSrc && isObjectStorage(mobileSrc) && <>
          <source media="(max-width: 767px)" type="image/avif" srcSet={buildObjectStorageSet(mobileSrc, 'avif')} sizes={sizes} />
          <source media="(max-width: 767px)" type="image/webp" srcSet={buildObjectStorageSet(mobileSrc, 'webp')} sizes={sizes} />
          <source media="(max-width: 767px)" type="image/jpeg" srcSet={buildObjectStorageSet(mobileSrc, 'jpeg')} sizes={sizes} />
        </>}
        {mobileSrc && !isObjectStorage(mobileSrc) && <source media="(max-width: 767px)" srcSet={mobileSrc} />}
        <source type="image/avif" srcSet={buildObjectStorageSet(src, 'avif')} sizes={sizes} />
        <source type="image/webp" srcSet={buildObjectStorageSet(src, 'webp')} sizes={sizes} />
        <img
          src={getDefaultSrc(src)}
          srcSet={buildObjectStorageSet(src, 'jpeg')}
          sizes={sizes}
          {...commonImgProps}
        />
      </picture>
    );
  }

  // Unsplash — single <img> with srcset (auto=format handles WebP CDN-side)
  if (isUnsplash(src)) {
    const image = <img src={src} srcSet={buildUnsplashSet(src)} sizes={sizes} {...commonImgProps} />;
    return mobileSrc ? <picture className={pictureClassName}><source media="(max-width: 767px)" srcSet={mobileSrc} />{image}</picture> : image;
  }

  // Any other URL (legacy /api/images/{id}, external, data URI) — plain <img>
  return (
    mobileSrc ? (
      <picture className={pictureClassName}>
        <source media="(max-width: 767px)" srcSet={mobileSrc} />
        <img src={src} {...commonImgProps} />
      </picture>
    ) : <img src={src} {...commonImgProps} />
  );
}
